import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, DragEndEvent, DragStartEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { LayoutGrid, Search, Settings2, Loader2, FileText, Pin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { KanbanColumn } from './KanbanColumn';
import { KanbanColumnManagerModal } from './KanbanColumnManagerModal';
import { KanbanFieldDialog } from './KanbanFieldDialog';
import { KanbanFieldDefinitionModal } from './KanbanFieldDefinitionModal';
import { kanbanService, KanbanColuna, KanbanTicket } from '../../services/kanbanService';
import { getFileUrl, getInitials } from '../../utils/dashboardHelpers';
import { useToast } from '../../hooks/use-toast';
import socketService from '../../services/socketService';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const PAGE_SIZE = 50;

interface ColunaData {
  tickets: KanbanTicket[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  offset: number;
}

interface KanbanBoardProps {
  className?: string;
  onTicketClick?: (ticket: KanbanTicket) => void;
}

const KanbanCardOverlay = ({ ticket }: { ticket: KanbanTicket }) => {
  const isOnline = ticket.instancia?.statusConexao === 'open';
  return (
    <div className="bg-white dark:bg-slate-700 rounded-lg p-3 border-2 border-purple-400 shadow-2xl max-w-[300px] opacity-95 rotate-2 select-none pointer-events-none">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={getFileUrl(ticket.avatar ?? undefined)} alt={ticket.nome} />
            <AvatarFallback className="bg-gray-200 dark:bg-slate-600 text-xs text-gray-600 dark:text-gray-300">
              {getInitials(ticket.nome)}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {ticket.nome}
          </span>
          {ticket.fixada && (
            <Pin size={12} className="text-yellow-500 flex-shrink-0" />
          )}
        </div>
      </div>
      {ticket.ultimaMensagem?.conteudo && (
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1 pl-10">
          {ticket.ultimaMensagem.conteudo.length > 40
            ? ticket.ultimaMensagem.conteudo.slice(0, 40) + '...'
            : ticket.ultimaMensagem.conteudo
          }
        </p>
      )}
    </div>
  );
};

const KanbanBoard = ({ className, onTicketClick }: KanbanBoardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [colunas, setColunas] = useState<KanbanColuna[]>([]);
  const [colunaData, setColunaData] = useState<Record<string, ColunaData>>({});
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [showFieldDefinitionModal, setShowFieldDefinitionModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<KanbanTicket | null>(null);
  const [activeTicket, setActiveTicket] = useState<KanbanTicket | null>(null);

  const [deleteConfirmColuna, setDeleteConfirmColuna] = useState<KanbanColuna | null>(null);
  const [deleteTicketCount, setDeleteTicketCount] = useState(0);

  const [fecharConfirmTicket, setFecharConfirmTicket] = useState<{ ticketId: string; colunaId: string } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadColunas = useCallback(async () => {
    setLoading(true);
    try {
      const colunasRes = await kanbanService.listarColunas();
      const cols = colunasRes.data.data || [];
      setColunas(cols);

      const initData: Record<string, ColunaData> = {};
      cols.forEach(c => {
        initData[c.id] = { tickets: [], total: 0, hasMore: false, loading: false, offset: 0 };
      });
      setColunaData(prev => {
        const merged = { ...prev };
        cols.forEach(c => {
          if (!merged[c.id]) merged[c.id] = initData[c.id];
        });
        return merged;
      });
    } catch (e: any) {
      toast({ title: 'Erro', description: e.response?.data?.message || 'Erro ao carregar colunas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadTicketsColuna = useCallback(async (colunaId: string, offset: number = 0, append: boolean = false) => {
    setColunaData(prev => ({
      ...prev,
      [colunaId]: { ...prev[colunaId], loading: true }
    }));
    try {
      const res = await kanbanService.listarTicketsPorColuna(colunaId, { search: search || undefined, limit: PAGE_SIZE, offset });
      const page = res.data;
      setColunaData(prev => ({
        ...prev,
        [colunaId]: {
          tickets: append ? [...(prev[colunaId]?.tickets || []), ...page.data] : page.data,
          total: page.total,
          hasMore: page.hasMore,
          loading: false,
          offset: append ? offset + page.data.length : page.data.length,
        }
      }));
    } catch {
      setColunaData(prev => ({
        ...prev,
        [colunaId]: { ...prev[colunaId], loading: false }
      }));
    }
  }, [search]);

  const loadAllColumns = useCallback(async () => {
    await loadColunas();
  }, [loadColunas]);

  useEffect(() => { loadAllColumns(); }, [loadAllColumns]);

  useEffect(() => {
    if (!loading && colunas.length > 0 && !initialLoadDone) {
      colunas.forEach(c => {
        loadTicketsColuna(c.id, 0, false);
      });
      setInitialLoadDone(true);
    }
  }, [loading, colunas, loadTicketsColuna, initialLoadDone]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const socketReloadTimeoutRef: { current: NodeJS.Timeout | null } = { current: null };

    const debouncedReload = () => {
      if (socketReloadTimeoutRef.current) clearTimeout(socketReloadTimeoutRef.current);
      socketReloadTimeoutRef.current = setTimeout(() => {
        colunas.forEach(c => loadTicketsColuna(c.id, 0, false));
      }, 2000);
    };

    socketService.on('chatListUpdated', debouncedReload);
    socketService.on('conversa_atualizada', debouncedReload);

    return () => {
      socketService.off('chatListUpdated', debouncedReload);
      socketService.off('conversa_atualizada', debouncedReload);
      if (socketReloadTimeoutRef.current) clearTimeout(socketReloadTimeoutRef.current);
    };
  }, [colunas, loadTicketsColuna]);

  const handleLoadMore = useCallback((colunaId: string) => {
    const data = colunaData[colunaId];
    if (data && data.hasMore && !data.loading) {
      loadTicketsColuna(colunaId, data.offset, true);
    }
  }, [colunaData, loadTicketsColuna]);

  const ticketsByColuna = useCallback((colunaId: string): KanbanTicket[] => {
    return colunaData[colunaId]?.tickets || [];
  }, [colunaData]);

  const colunaTotalCount = useCallback((colunaId: string): number => {
    return colunaData[colunaId]?.total || 0;
  }, [colunaData]);

  const findColunaTitulo = useCallback((colunaId: string): string | null => {
    return colunas.find(c => c.id === colunaId)?.titulo || null;
  }, [colunas]);

  const isAbertosColuna = useCallback((colunaId: string): boolean => {
    return findColunaTitulo(colunaId) === 'Abertos';
  }, [findColunaTitulo]);

  const isPendentesColuna = useCallback((colunaId: string): boolean => {
    return findColunaTitulo(colunaId) === 'Pendentes';
  }, [findColunaTitulo]);

  const isFechadosColuna = useCallback((colunaId: string): boolean => {
    return findColunaTitulo(colunaId) === 'Fechados';
  }, [findColunaTitulo]);

  const canMoveToPendentes = useCallback((fromColunaId: string): boolean => {
    return !isAbertosColuna(fromColunaId);
  }, [isAbertosColuna]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const ticket = event.active.data.current?.ticket as KanbanTicket;
    if (ticket) setActiveTicket(ticket);
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTicket(null);
    if (!over) return;

    const ticketId = active.id as string;
    const ticket = Object.values(colunaData).flatMap(d => d.tickets).find(t => t.id === ticketId);
    if (!ticket) return;

    const overData = over.data.current;
    const overIsTicket = !!overData?.ticket;
    const overIsColumn = !!overData?.coluna;

    if (overIsTicket) {
      const targetTicket = overData.ticket as KanbanTicket;
      const fromColunaId = ticket.kanbanColunaId;
      const toColunaId = targetTicket.kanbanColunaId;
      if (!fromColunaId || !toColunaId) return;

      if (fromColunaId === toColunaId) {
        const colunaTickets = colunaData[fromColunaId]?.tickets || [];
        const oldIdx = colunaTickets.findIndex(t => t.id === ticketId);
        const newIdx = colunaTickets.findIndex(t => t.id === targetTicket.id);
        if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;

        const newOrder = arrayMove(colunaTickets, oldIdx, newIdx);

        setColunaData(prev => ({
          ...prev,
          [fromColunaId]: { ...prev[fromColunaId], tickets: newOrder }
        }));

        try {
          await kanbanService.reordenarTickets(newOrder.map((t, i) => ({ id: t.id, ordem: i })));
        } catch {
          loadTicketsColuna(fromColunaId, 0, false);
        }
        return;
      }

      if (isPendentesColuna(toColunaId) && isAbertosColuna(fromColunaId)) {
        toast({ title: 'Ação não permitida', description: 'Não é possível voltar de Abertos para Pendentes', variant: 'destructive' });
        return;
      }

      if (isFechadosColuna(toColunaId)) {
        setFecharConfirmTicket({ ticketId, colunaId: toColunaId });
        return;
      }

      setColunaData(prev => ({
        ...prev,
        [fromColunaId]: {
          ...prev[fromColunaId],
          tickets: prev[fromColunaId].tickets.filter(t => t.id !== ticketId),
          total: Math.max(0, (prev[fromColunaId]?.total || 1) - 1),
        },
        [toColunaId]: {
          ...prev[toColunaId],
          tickets: [{ ...ticket, kanbanColunaId: toColunaId }, ...prev[toColunaId].tickets],
          total: (prev[toColunaId]?.total || 0) + 1,
        }
      }));

      try {
        await kanbanService.moverTicket(ticketId, { kanbanColunaId: toColunaId });
        loadTicketsColuna(fromColunaId, 0, false);
        loadTicketsColuna(toColunaId, 0, false);
      } catch {
        toast({ title: 'Erro', description: 'Erro ao mover ticket', variant: 'destructive' });
        loadTicketsColuna(fromColunaId, 0, false);
        loadTicketsColuna(toColunaId, 0, false);
      }
      return;
    }

    const targetColunaId = over.id as string;
    if (!overIsColumn) return;

    const fromColunaId = ticket.kanbanColunaId;
    if (!fromColunaId || fromColunaId === targetColunaId) return;

    if (isPendentesColuna(targetColunaId) && isAbertosColuna(fromColunaId)) {
      toast({ title: 'Ação não permitida', description: 'Não é possível voltar de Abertos para Pendentes', variant: 'destructive' });
      return;
    }

    if (isFechadosColuna(targetColunaId)) {
      setFecharConfirmTicket({ ticketId, colunaId: targetColunaId });
      return;
    }

    setColunaData(prev => ({
      ...prev,
      [fromColunaId]: {
        ...prev[fromColunaId],
        tickets: prev[fromColunaId].tickets.filter(t => t.id !== ticketId),
        total: Math.max(0, (prev[fromColunaId]?.total || 1) - 1),
      },
      [targetColunaId]: {
        ...prev[targetColunaId],
        tickets: [{ ...ticket, kanbanColunaId: targetColunaId }, ...prev[targetColunaId].tickets],
        total: (prev[targetColunaId]?.total || 0) + 1,
      }
    }));

    try {
      await kanbanService.moverTicket(ticketId, { kanbanColunaId: targetColunaId });
      loadTicketsColuna(fromColunaId, 0, false);
      loadTicketsColuna(targetColunaId, 0, false);
    } catch {
      toast({ title: 'Erro', description: 'Erro ao mover ticket', variant: 'destructive' });
      loadTicketsColuna(fromColunaId, 0, false);
      loadTicketsColuna(targetColunaId, 0, false);
    }
  }, [colunaData, isPendentesColuna, isAbertosColuna, isFechadosColuna, toast, loadTicketsColuna]);

  const handleConfirmarFecharTicket = useCallback(async () => {
    if (!fecharConfirmTicket) return;
    const { ticketId, colunaId } = fecharConfirmTicket;
    setFecharConfirmTicket(null);

    const ticket = Object.values(colunaData).flatMap(d => d.tickets).find(t => t.id === ticketId);
    if (!ticket) return;

    const fromColunaId = ticket.kanbanColunaId;

    if (fromColunaId) {
      setColunaData(prev => ({
        ...prev,
        [fromColunaId]: {
          ...prev[fromColunaId],
          tickets: prev[fromColunaId].tickets.filter(t => t.id !== ticketId),
          total: Math.max(0, (prev[fromColunaId]?.total || 1) - 1),
        }
      }));
    }

    try {
      await kanbanService.moverTicket(ticketId, { kanbanColunaId: colunaId, status: 'fechado' });
      toast({ title: 'Sucesso', description: 'Ticket fechado com sucesso' });
      if (fromColunaId) loadTicketsColuna(fromColunaId, 0, false);
      loadTicketsColuna(colunaId, 0, false);
    } catch {
      toast({ title: 'Erro', description: 'Erro ao fechar ticket', variant: 'destructive' });
      if (fromColunaId) loadTicketsColuna(fromColunaId, 0, false);
      loadTicketsColuna(colunaId, 0, false);
    }
  }, [fecharConfirmTicket, colunaData, toast, loadTicketsColuna]);

  const handleMoverTicket = useCallback(async (ticketId: string, colunaId: string | null, status?: string) => {
    if (!colunaId) return;

    const ticket = Object.values(colunaData).flatMap(d => d.tickets).find(t => t.id === ticketId);
    if (!ticket) return;

    const fromColunaId = ticket.kanbanColunaId;

    if (fromColunaId && isPendentesColuna(colunaId) && isAbertosColuna(fromColunaId)) {
      toast({ title: 'Ação não permitida', description: 'Não é possível voltar de Abertos para Pendentes', variant: 'destructive' });
      return;
    }

    if (isFechadosColuna(colunaId)) {
      setFecharConfirmTicket({ ticketId, colunaId });
      return;
    }

    if (fromColunaId) {
      setColunaData(prev => ({
        ...prev,
        [fromColunaId]: {
          ...prev[fromColunaId],
          tickets: prev[fromColunaId].tickets.filter(t => t.id !== ticketId),
          total: Math.max(0, (prev[fromColunaId]?.total || 1) - 1),
        },
        [colunaId]: {
          ...prev[colunaId],
          tickets: [{ ...ticket, kanbanColunaId: colunaId, ...(status ? { status } : {}) }, ...prev[colunaId].tickets],
          total: (prev[colunaId]?.total || 0) + 1,
        }
      }));
    }

    try {
      await kanbanService.moverTicket(ticketId, { kanbanColunaId: colunaId, status });
      if (fromColunaId) loadTicketsColuna(fromColunaId, 0, false);
      loadTicketsColuna(colunaId, 0, false);
    } catch {
      if (fromColunaId) loadTicketsColuna(fromColunaId, 0, false);
      loadTicketsColuna(colunaId, 0, false);
    }
  }, [colunaData, isPendentesColuna, isAbertosColuna, isFechadosColuna, toast, loadTicketsColuna]);

  const handleToggleMinimize = useCallback(async (coluna: KanbanColuna) => {
    const novoEstado = !coluna.minimizada;
    setColunas(prev => prev.map(c => c.id === coluna.id ? { ...c, minimizada: novoEstado } : c));
    try {
      await kanbanService.atualizarColuna(coluna.id, { minimizada: novoEstado });
    } catch { loadColunas(); }
  }, [loadColunas]);

  const handleColumnMenu = useCallback(() => { setShowColumnManager(true); }, []);

  const handleAbrirCampos = useCallback((ticket: KanbanTicket) => {
    setSelectedTicket(ticket);
    setShowFieldDialog(true);
  }, []);

  const handleAbrirAtendimento = useCallback((ticket: KanbanTicket) => {
    if (onTicketClick) onTicketClick(ticket);
    else navigate('/dashboard', { state: { ticketId: ticket.id } });
  }, [navigate, onTicketClick]);

  const handleCriarColuna = useCallback(async (data: { titulo: string; cor: string }) => {
    await kanbanService.criarColuna(data);
    loadColunas();
  }, [loadColunas]);

  const handleAtualizarColuna = useCallback(async (id: string, data: { titulo?: string; cor?: string }) => {
    await kanbanService.atualizarColuna(id, data);
    loadColunas();
  }, [loadColunas]);

  const handleExcluirColuna = useCallback(async (id: string) => {
    try {
      await kanbanService.excluirColuna(id);
      toast({ title: 'Sucesso', description: 'Coluna excluída com sucesso' });
      loadColunas();
    } catch (e: any) {
      toast({ title: 'Erro', description: e.response?.data?.message || 'Erro ao excluir coluna', variant: 'destructive' });
    }
  }, [loadColunas, toast]);

  const handleRequestExcluirColuna = useCallback((coluna: KanbanColuna) => {
    const count = colunaTotalCount(coluna.id) || 0;
    if (count > 0) {
      setDeleteTicketCount(count);
      setDeleteConfirmColuna(coluna);
    } else {
      handleExcluirColuna(coluna.id);
    }
  }, [colunaTotalCount, handleExcluirColuna]);

  const handleReordenarColunas = useCallback(async (colunasReordenadas: Array<{ id: string; ordem: number }>) => {
    try {
      await kanbanService.reordenarColunas(colunasReordenadas);
      loadColunas();
    } catch {
      toast({ title: 'Erro', description: 'Erro ao reordenar colunas', variant: 'destructive' });
      loadColunas();
    }
  }, [loadColunas, toast]);

  const handleConfirmarFecharFromCard = useCallback((ticketId: string, colunaId: string) => {
    setFecharConfirmTicket({ ticketId, colunaId });
  }, []);

  const handleFixarTicket = useCallback(async (ticketId: string) => {
    try {
      const res = await kanbanService.fixarDesfixarTicket(ticketId);
      const newFixada = res.data.data.fixada;
      setColunaData(prev => {
        const updated = { ...prev };
        for (const colId of Object.keys(updated)) {
          updated[colId] = {
            ...updated[colId],
            tickets: updated[colId].tickets.map(t =>
              t.id === ticketId ? { ...t, fixada: newFixada } : t
            )
          };
        }
        return updated;
      });
    } catch {
      toast({ title: 'Erro', description: 'Erro ao fixar ticket', variant: 'destructive' });
    }
  }, [toast]);

  return (
    <div className={`flex flex-col ${className || ''}`}>
      <div className="flex items-center gap-3 px-4 pt-3 pb-2 flex-shrink-0">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar tickets..."
            className="h-9 pl-9 pr-3 text-sm dark:bg-slate-700 dark:text-white dark:border-slate-600"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFieldDefinitionModal(true)} className="dark:bg-slate-700 dark:text-white dark:border-slate-600">
          <FileText size={14} className="mr-1.5" />Gerenciar Campos
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowColumnManager(true)} className="dark:bg-slate-700 dark:text-white dark:border-slate-600">
          <Settings2 size={14} className="mr-1.5" />Gerenciar Colunas
        </Button>
      </div>

      <div className="flex-1 overflow-x-auto overflow-y-hidden px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : colunas.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <LayoutGrid size={48} className="mb-4 opacity-30" />
            <h2 className="text-xl font-light mb-2">Nenhuma coluna encontrada</h2>
            <p className="text-sm mb-4">Crie colunas para organizar seus tickets</p>
            <Button onClick={() => setShowColumnManager(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Settings2 size={14} className="mr-1.5" />Gerenciar Colunas
            </Button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <DragOverlay dropAnimation={null}>
              {activeTicket ? <KanbanCardOverlay ticket={activeTicket} /> : null}
            </DragOverlay>
            <div className="flex gap-4 h-full" style={{ position: 'relative', zIndex: 0 }}>
              {colunas.map(coluna => (
                <KanbanColumn
                  key={coluna.id}
                  coluna={coluna}
                  tickets={ticketsByColuna(coluna.id)}
                  totalCount={colunaTotalCount(coluna.id)}
                  hasMore={colunaData[coluna.id]?.hasMore || false}
                  isLoadingMore={colunaData[coluna.id]?.loading || false}
                  onLoadMore={() => handleLoadMore(coluna.id)}
                  todasColunas={colunas}
                  onToggleMinimize={handleToggleMinimize}
                  onColumnMenu={handleColumnMenu}
                  onMoverTicket={handleMoverTicket}
                  onAbrirCampos={handleAbrirCampos}
                  onAbrirNota={handleAbrirCampos}
                  onAbrirAtendimento={handleAbrirAtendimento}
                  onCardClick={handleAbrirAtendimento}
                  onExcluirColuna={handleRequestExcluirColuna}
                  onConfirmarFechar={handleConfirmarFecharFromCard}
                  canMoveToPendentes={canMoveToPendentes}
                  onFixar={handleFixarTicket}
                />
              ))}
            </div>
          </DndContext>
        )}
      </div>

      <KanbanColumnManagerModal
        open={showColumnManager}
        onOpenChange={setShowColumnManager}
        colunas={colunas}
        onCriar={handleCriarColuna}
        onAtualizar={handleAtualizarColuna}
        onExcluir={handleRequestExcluirColuna}
        onReordenar={handleReordenarColunas}
      />

      <KanbanFieldDefinitionModal open={showFieldDefinitionModal} onOpenChange={setShowFieldDefinitionModal} />

      <KanbanFieldDialog open={showFieldDialog} onOpenChange={setShowFieldDialog} ticket={selectedTicket} />

      <AlertDialog open={!!deleteConfirmColuna} onOpenChange={(open) => { if (!open) { setDeleteConfirmColuna(null); setDeleteTicketCount(0); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Não é possível excluir</AlertDialogTitle>
            <AlertDialogDescription>
              A coluna <strong>"{deleteConfirmColuna?.titulo}"</strong> possui{' '}
              <strong>{deleteTicketCount}</strong> ticket(s) vinculado(s).
              É necessário mover ou desvincular todos os tickets antes de excluir esta coluna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirmColuna(null); setDeleteTicketCount(0); }}>Entendi</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!fecharConfirmTicket} onOpenChange={(open) => { if (!open) setFecharConfirmTicket(null); }}>
        <AlertDialogContent className="dark:bg-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">Fechar Ticket</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-300">
              Tem certeza que deseja mover este ticket para <strong>Fechados</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFecharConfirmTicket(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmarFecharTicket} className="bg-red-600 hover:bg-red-700 text-white">
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KanbanBoard;
